/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef } from 'react';
import MonsterGrid from './MonsterGrid';
import searchImg from 'dndimages/search.png';
import DataModel from 'model/DataModel';
import Monster from 'model/monster/Monster';

interface MonsterSearchProps {
  isOpen: boolean;
  minCr: number | null;
  maxCr: number | null;
  keywords: string;
  selectedEnvironments: string[];
  pageSize: number | null;
  currentPage: number | null;
  sortBy: { id: string; desc: boolean }[];
  dataModel: DataModel;
  myMonsters: Monster[];
  communityMonsters: { username: string; monster: Monster }[];
  onUpdateMinCr: (minCr: number) => void;
  onUpdateMaxCr: (maxCr: number) => void;
  onUpdateKeywords: (keywords: string) => void;
  onUpdateSelectedEnvironments: (environments: string[]) => void;
  onUpdatePageSize: (pageSize: number) => void;
  onUpdateCurrentPage: (currentPage: number) => void;
  onUpdateSort: (sortBy: { id: string; desc: boolean }[]) => void;
  setIsOpen: (isOpen: boolean) => void;
  handleMonsterChange: (monsterId: string) => void;
  onStartTour?: () => void;   // launches the dedicated grid-search tour from the '?' icon
  tourActive?: boolean;       // while that tour runs, don't close the modal on outside clicks
}

const MonsterSearch: React.FC<MonsterSearchProps> = ({isOpen, minCr, maxCr, selectedEnvironments, keywords, pageSize,
                                                      currentPage, sortBy, dataModel, myMonsters, communityMonsters,
                                                      onUpdateMinCr, onUpdateMaxCr, onUpdateKeywords, onUpdateSelectedEnvironments,
                                                      onUpdatePageSize, onUpdateCurrentPage, onUpdateSort, setIsOpen, handleMonsterChange,
                                                      onStartTour, tourActive }) => {
  const popupPanelRef = useRef<HTMLSpanElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  // Mirror tourActive into a ref so the (once-registered) outside-click handler sees the latest value.
  const tourActiveRef = useRef<boolean>(false);
  useEffect(() => { tourActiveRef.current = !!tourActive; }, [tourActive]);

  const handleClickOutside = (event: MouseEvent) => {
    if (tourActiveRef.current) {
      return; // keep the modal open while the grid-search tour is running
    }
    if (
      popupPanelRef.current &&
      !popupPanelRef.current.contains(event.target as Node) &&
      imageRef.current &&
      !imageRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <img
        src={searchImg}
        alt="Advanced Monster Search"
        style={{ width: '32px', cursor: 'pointer' }}
        onClick={() => setIsOpen(true)}
        ref={imageRef}
        id='monsterGridSearch'
      />
      {isOpen && (
        <span className="multi-select-container edit-field" id='AdvancedMonsterGrid'>
          <span className="monster-search-panel" ref={popupPanelRef}>
            {onStartTour && (
              <button type="button" onClick={onStartTour} id="gridSearchTourButton"
                      title="Take a quick tour of the monster search"
                      className="tour-button" style={{ position: 'absolute', top: '6px', right: '10px', zIndex: 5 }}>
                ?
              </button>
            )}
            <MonsterGrid onSelectMonster={monsterId => {handleMonsterChange(monsterId); setIsOpen(false)}}
                         minCr={minCr} maxCr={maxCr} keywords={keywords} selectedEnvironments={selectedEnvironments}
                         pageSize={pageSize} currentPage={currentPage} sortBy={sortBy}
                         onUpdateMinCr={onUpdateMinCr} onUpdateMaxCr={onUpdateMaxCr} onUpdateKeywords={onUpdateKeywords}
                         onUpdateSelectedEnvironments={onUpdateSelectedEnvironments} onUpdateSort={onUpdateSort} 
                         onUpdatePageSize={onUpdatePageSize} onUpdateCurrentPage={onUpdateCurrentPage}
                         dataModel={dataModel} myMonsters={myMonsters} communityMonsters={communityMonsters}
                       />
          </span>
        </span>
      )}
    </>
  );
};

export default MonsterSearch;
